import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useContext } from 'react';
import { AuthContext } from '../../../AuthContext/Authcontext';
import ReusableTable from '../../../Layout/ReusableTable';
import baseURL from "../../../Apiurs/Apiurls";
import Navbar from "../../../Shared/AssociateNavbar/Navbar";
import { FaEye, FaSave, FaEdit, FaTrash, FaChevronDown, FaChevronRight, FaFileExcel, FaComment } from "react-icons/fa";
import './Leads.css';


function AssociateLeads() { 
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState([]);
  const [message, setMessage] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedAssociate, setSelectedAssociate] = useState({});
  const [assignedData, setAssignedData] = useState({});
  const [associateChanged, setAssociateChanged] = useState({});
    const [statusChanged, setStatusChanged] = useState({});
      const [selectedStatus, setSelectedStatus] = useState({});

  const navigate = useNavigate();
  const { authToken, userId, userRole } = useContext(AuthContext);

  // Fetch team members (including self)
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/employees/managers`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        // Find the logged-in manager's team
        const manager = response.data.data.find(m => m.id.toString() === userId.toString()) || {};
        const members = manager.teamMembers || [];
        // Include self as an option
        const self = { id: userId, name: 'Self' };
        setTeamMembers([self, ...members]);
      } catch (error) {
        console.error('Error fetching team members:', error);
        setMessage('Failed to fetch team members');
      }
    };
    fetchTeamMembers();
  }, [authToken, userId]);

  // Fetch leads and assignments
  const fetchLeads = async () => {
    try {
      // Fetch leads for the specific userId
      const leadsResponse = await axios.get(`${baseURL}/api/contacts/new/${userId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Fetch assignments
      const assignmentsResponse = await axios.get(`${baseURL}/api/lead-assignments`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (leadsResponse.status === 200) {
        const leads = leadsResponse.data;
        setData(leads);
        setFilteredData(leads);

        const associateSelections = {};
         const statusSelections = {};
        const assignments = assignmentsResponse.data;

        leads.forEach((lead) => {
          const assignment = assignments[lead.id] || {};
          associateSelections[lead.id] = assignment.associate_id?.toString() || lead.assigned_to?.toString() || '';
           statusSelections[lead.id] = lead.status || 'New'; // Default to 'New' if no status
        });

        setSelectedAssociate(associateSelections);
          setSelectedStatus(statusSelections);
        setAssignedData(assignments);
      }
    } catch (error) {
      console.error('Error fetching leads or assignments:', error);
      setMessage(error.response?.data?.message || 'Failed to fetch leads or assignments');
    }
  };

  // Fetch leads and assignments initially
  useEffect(() => {
    if (userId) {
      fetchLeads();
    }
  }, [authToken, userId, teamMembers.length]);

  // Handle associate assignment
  const handleAssociateAssignment = async (leadId) => {
    try {
      const assignedTo = selectedAssociate[leadId];
      if (!assignedTo) {
        setMessage('Please select an associate');
        return;
      }

      const response = await axios.put(
        `${baseURL}/api/leadcrm/${leadId}/assign-user`,
        {
          assigned_by: userId,
          assigned_to: assignedTo,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setMessage('Associate assignment saved successfully');
      setAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
      setAssignedData((prev) => ({
        ...prev,
        [leadId]: response.data.data,
      }));
      // Update lead data to reflect new assignment
      setData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, assigned_by: userId.toString(), assigned_to: assignedTo }
            : lead
        )
      );
      setFilteredData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, assigned_by: userId.toString(), assigned_to: assignedTo }
            : lead
        )
      );
    } catch (error) {
      console.error('Error assigning associate:', error);
      setMessage(error.response?.data?.message || 'Failed to save associate assignment');
    }
  };

  // Handle edit action
  const handleEdit = (row) => {
    navigate(`/a-edit-lead/${row.id}`);
  };

  // Handle delete action
  const handleDelete = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await axios.delete(`${baseURL}/api/contacts/${leadId}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setMessage('Lead deleted successfully');
        fetchLeads();
      } catch (error) {
        console.error('Error deleting lead:', error);
        setMessage(error.response?.data?.message || 'Failed to delete lead');
      }
    }
  };

  // Handle add lead
  const handleAddUser = () => {
    navigate('/s-add-lead');
  };

  // Reusable truncate cell
  const TruncateCell = ({ value, maxLength = 20 }) => {
    if (value === null || value === undefined) {
      return <span>-</span>;
    }
    const str = String(value);
    return (
      <div
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: `${maxLength * 8}px`,
        }}
        title={str}
      >
        {str.length > maxLength ? str.slice(0, maxLength) + '...' : str}
      </div>
    );
  };

   // Handle status update
  const handleStatusUpdate = async (leadId) => {
    try {
      const newStatus = selectedStatus[leadId];
      
      const response = await axios.put(
        `${baseURL}/api/contacts/${leadId}/status`,
        {
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setMessage('Status updated successfully');
      setStatusChanged((prev) => ({ ...prev, [leadId]: false }));
      
      // Update the data with the new status
      setData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, status: newStatus }
            : lead
        )
      );
      setFilteredData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, status: newStatus }
            : lead
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
      setMessage(error.response?.data?.message || 'Failed to update status');
    }
  };

  // Table columns
  const columns = [
    {
      key: 'leadId',
      title: 'LeadID',
      render: (row) => <TruncateCell value={`Lead00${row.id}`} maxLength={10} />,
    },
    {
      key: 'lead_name',
      title: 'Lead Name',
      render: (row) => <TruncateCell value={row.lead_name} maxLength={15} />,
    },
    {
      key: 'business_name',
      title: 'Business Name',
      render: (row) => <TruncateCell value={row.business_name} maxLength={15} />,
    },
    {
      key: 'lead_source',
      title: 'Source',
      render: (row) => <TruncateCell value={row.lead_source} maxLength={12} />,
    },
    {
      key: 'contact_number',
      title: 'Contact Number',
      render: (row) => <TruncateCell value={row.contact_number} maxLength={12} />,
    },
    {
      key: 'email',
      title: 'Email',
      render: (row) => <TruncateCell value={row.email} maxLength={20} />,
    },
    {
      key: 'preferred_contact_method',
      title: 'Contact Method',
      render: (row) => <TruncateCell value={row.preferred_contact_method} maxLength={15} />,
    },

    {
          key: 'status',
          title: 'Status',
          render: (row) => {
            const leadId = row.id;
            return (
              <div className="d-flex align-items-center gap-2" style={{ minWidth: '190px' }}>
                <select
                  className="form-select form-select-sm"
                  value={selectedStatus[leadId] || 'New'}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setSelectedStatus((prev) => ({ ...prev, [leadId]: newStatus }));
                    setStatusChanged((prev) => ({ ...prev, [leadId]: true }));
                  }}
                >
                  <option value="New">New</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Not Qualified">Not Qualified</option>
                  <option value="Loss">Loss</option>
                </select>
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => handleStatusUpdate(leadId)}
                  disabled={!statusChanged[leadId]}
                  title="Save Status"
                >
                  <FaSave />
                </button>
              </div>
            );
          },
        },

    {
      key: 'actions',
      title: 'Actions',
      render: (row) => (
        <div className="d-flex gap-2">
              <button
           className="btn btn-sm btn-outline-primary"
           onClick={() =>
             navigate(`/s-newviewlead/${row.id}`, { state: { lead: row } })
           }
           title="View"
         >
           <FaEye />
         </button>
          {/* <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleEdit(row)}
            title="Edit"
          >
            <FaEdit />
          </button> */}
          {/* <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row.id)}
            title="Delete"
          >
            <FaTrash />
          </button> */}
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigate(`/s-comments/${row.id}`, { state: { leadid: row.id } })}
            title="Comment"
          >
            <FaComment />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="AssociateLeadscontainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`AssociateLeads ${collapsed ? 'collapsed' : ''}`}>
        <div className="AssociateLeads-container mb-5">
          <div className="AssociateLeads-table-container">
            <div className="d-flex justify-content-end mb-3">
              <button className="btn btn-success" onClick={handleAddUser}>
                + Add Lead
              </button>
            </div>
            {message && (
              <div className={`alert ${message.includes('successfully') ? 'alert-success' : 'alert-danger'}`}>
                {message}
              </div>
            )}
            <ReusableTable
              title="Associate Leads"
              data={filteredData}
              columns={columns}
              initialEntriesPerPage={5}
              searchPlaceholder="Search leads..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssociateLeads;
